import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import api from '../services/api';

interface ImageUploaderProps {
  onImageSelect: (imageUrl: string) => void;
  currentImageUrl?: string;
  placeholder?: string;
  accept?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageSelect,
  currentImageUrl,
  placeholder = "Glissez une image ici ou cliquez pour sélectionner",
  accept = "image/*"
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>(currentImageUrl || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image valide');
      return;
    }

    setIsUploading(true);
    
    try {
      // Créer une URL temporaire pour l'aperçu immédiat
      const tempUrl = URL.createObjectURL(file);
      setPreviewUrl(tempUrl);

      // Upload vers le serveur
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await api.post('/api/upload-image/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      
      // Utiliser l'URL du serveur
      setPreviewUrl(result.image_url);
      onImageSelect(result.image_url);
      setIsUploading(false);
      
      // Nettoyer l'URL temporaire
      URL.revokeObjectURL(tempUrl);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'upload:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur inconnue';
      alert(`Erreur lors de l'upload de l'image: ${errorMessage}`);
      setIsUploading(false);
      setPreviewUrl('');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeImage = () => {
    setPreviewUrl('');
    onImageSelect('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      {/* Zone de drop */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${isUploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
        />
        
        {isUploading ? (
          <div className="flex flex-col items-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-600">Upload en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <Upload className="w-8 h-8 text-gray-400" />
            <p className="text-sm text-gray-600">{placeholder}</p>
            <p className="text-xs text-gray-500">PNG, JPG, GIF jusqu'à 10MB</p>
          </div>
        )}
      </div>

      {/* Aperçu de l'image */}
      {previewUrl && (
        <div className="relative">
          <div className="relative inline-block">
            <img
              src={previewUrl}
              alt="Aperçu"
              className="max-w-full h-32 object-cover rounded-lg border border-gray-200"
            />
            <button
              onClick={removeImage}
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Cliquez sur X pour supprimer l'image
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
